import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import useWebSocket from '@/utils/websocket';
import { setHistoryMsg } from '@/store/slices/room';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import Config from '@/config';
import { audioPlayer } from '@/utils/AudioPlayer';
import Utils from '@/utils/utils';

interface AppContentType {
  sendMessage: (message: string, prompt: string) => void;
  sendReplayMessage: (message: string, recordId: string) => void;
  sendText2Audio: (message: string) => void;
  initLLM: (message: string) => void;
  switchRole: () => void;
  interrupt: () => void;
  inPlaying: boolean;
}

const AppContent = createContext<AppContentType | undefined>(undefined);

export function AppContentProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const { roomId } = useSelector((state: RootState) => state.room);
  const roleId = localStorage.getItem('roleId') || '';
  const userInfoString = localStorage.getItem('userInfo') || '{}';
  const userInfo = JSON.parse(userInfoString);
  const [inPlaying, setInPlaying] = useState(false);
  // 单例播放器
  const messageCache = useRef('');

  useEffect(() => {
    audioPlayer.onStart(() => {
      setInPlaying(true);
    });

    audioPlayer.onStop(() => {
      setInPlaying(false);
    });
  }, []);

  /**
   * 统一处理音频数据
   */
  const handleAudioData = async (data: Blob) => {
    try {
      data.arrayBuffer().then((buffer) => {
        audioPlayer.receiveAudioData(buffer);
      });
    } catch (error) {
      console.error('音频数据处理失败:', error);
    }
  };

  /**
   * Handles incoming WebSocket messages
   */
  const onLlmMessage = (event: MessageEvent) => {
    if (event.data instanceof Blob) {
      return handleAudioData(event.data);
    }

    let parsedData;

    if (typeof event.data === 'string') {
      try {
        parsedData = JSON.parse(event.data);
      } catch (error) {
        console.error('Error parsing JSON string:', error);
        return;
      }
    } else if (typeof event.data === 'object' && event.data !== null) {
      parsedData = event.data;
    } else {
      console.warn('Unsupported message type:', typeof event.data);
      return;
    }

    if (parsedData.type === 'text') {
      if (parsedData.isFirst) {
        messageCache.current = '';
        audioPlayer.reload();
      }

      messageCache.current += parsedData.content;

      const payload = {
        recordId: parsedData.recordId || Utils.generateUUID(),
        type: parsedData.type || 'text',
        persona_id: roleId,
        room_id: roomId || '',
        token: '',
        isReplay: parsedData.isReplay,
        value: messageCache.current,
        text: messageCache.current,
        time: new Date().toString(),
        user: `${Config.BotName}`,
        paragraph: true,
        definite: parsedData.isLast,
      };

      dispatch(setHistoryMsg(payload));
    } else {
      console.warn('Received JSON message without textChunk:', parsedData);
    }
  };

  const onLlmError = (event: Event) => {
    console.error('WebSocket error:', event);
  };

  const { socket, isConnected } = useWebSocket('wss://api.xmyunan.com/api/1067/llm2tts', {
    // const { socket, isConnected } = useWebSocket('ws://localhost:8080/api/llm2tts', {
    onMessage: onLlmMessage,
    onError: onLlmError,
  });

  /**
   * Sends a message to the LLM for processing
   */
  const sendMessage = (message: string, prompt?: string) => {
    if (!isConnected) return;

    const token = localStorage.getItem('token');
    socket?.send(
      JSON.stringify({
        type: 'llm2audio',
        prompt: prompt || '',
        message,
        token,
        userId: userInfo.id,
        roleId: roleId,
      })
    );
  };

  /**
   * Converts text to audio
   * Stops any currently playing audio before sending the request
   */
  const sendText2Audio = (text: string) => {
    if (!isConnected) return;
    const token = localStorage.getItem('token');
    socket?.send(JSON.stringify({ type: 'text2audio', message: text, token, userId: userInfo.id, roleId: roleId }));
    audioPlayer.reload();
  };

  const switchRole = () => {
    if (!isConnected) return;
    socket?.send(JSON.stringify({ type: 'interrupt-llm2voice', userId: userInfo.id, roleId: roleId }));
    audioPlayer.reload();
  };

  const interrupt = () => {
    if (!isConnected) return;
    socket?.send(JSON.stringify({ type: 'interrupt-llm2voice', userId: userInfo.id, roleId: roleId }));
    setTimeout(() => {
      audioPlayer.reload();
    }, 100)
  };

  const sendReplayMessage = (message: string, recordId: string) => {
    if (!isConnected) return;
    socket?.send(
      JSON.stringify({
        type: 'llm2audio',
        userId: userInfo.id,
        roleId: roleId,
        recordId,
        message,
        isReplay: true,
        prompt: '这是你给我的答案,我不满意这个答案,请重新生成',
      })
    );
    audioPlayer.reload();
  };

  /**
   * Initializes the LLM with a prompt
   */
  const initLLM = (prompt: string) => {
    if (!isConnected) return;
    socket?.send(JSON.stringify({ action: 'init', prompt: prompt }));
  };

  return (
    <AppContent.Provider
      value={{
        sendMessage,
        sendText2Audio,
        initLLM,
        switchRole,
        inPlaying,
        interrupt,
        sendReplayMessage,
      }}
    >
      {children}
    </AppContent.Provider>
  );
}

export function useAppContent() {
  const context = useContext(AppContent);
  if (context === undefined) {
    throw new Error('useAppContent must be used within a AppProvider');
  }
  return context;
}
